import React, {useMemo, useState, useEffect} from 'react';
import axios from 'axios';
import {ColumnDef} from '@tanstack/react-table';
import {DataGrid, KeenIcon} from '@/components';
import {Loading} from '@/layouts/buddy/loading';
import {FeedbackData} from '@/pages/buddy/types/FeedbackData';
import {formatDate} from '@/pages/buddy/utils/Date.tsx';
import {enqueueSnackbar} from 'notistack';

interface FeedbacksProps {
    feedbacks: FeedbackData[];
    loading: boolean;
}

const Feedbacks: React.FC<FeedbacksProps> = ({feedbacks, loading}) => {
    const [searchTerm, setSearchTerm] = useState(
        () => localStorage.getItem('feedbacks-filter') || ''
    );

    useEffect(() => {
        localStorage.setItem('feedbacks-filter', searchTerm);
    }, [searchTerm]);

    const filteredData = useMemo(
        () =>
            feedbacks.filter((feedback) => {
                const text = `${feedback.user_email} ${feedback.feedback}`.toLowerCase();
                return text.includes(searchTerm.toLowerCase());
            }),
        [feedbacks, searchTerm]
    );

    const handleFileDownload = async (url: string, fileName: string) => {
        try {
            const fullUrl = `${import.meta.env.VITE_APP_API_URL}${url}`
            const token = localStorage.getItem('authToken')

            const response = await axios.get(fullUrl, {
                responseType: 'blob',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            })

            const blob = new Blob([response.data])
            const downloadLink = document.createElement('a')
            downloadLink.href = URL.createObjectURL(blob)
            downloadLink.download = fileName
            downloadLink.click()
            URL.revokeObjectURL(downloadLink.href)
        } catch (error) {
            console.error('Download error:', error)
            enqueueSnackbar('Failed to download the file.', {
                variant: 'solid',
                state: 'danger',
            })
        }
    }

    const columns = useMemo<ColumnDef<FeedbackData>[]>(() => [
        {
            accessorKey: 'user_name',
            header: 'Name', enableSorting: true,
            cell: ({getValue}) => getValue() || '—',
        },
        {
            accessorKey: 'user_email',
            header: 'Email', enableSorting: true,
},
        {
            accessorKey: 'metadata.url',
            header: 'URL', enableSorting: true,
            cell: ({row}) => (
                <a
                    href={row.original.metadata?.url || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary"
                >
                    {row.original.metadata?.url || '—'}
                </a>
            ),
        },
        {
            accessorKey: 'created_at',
            header: 'Submitted', enableSorting: true,
            cell: ({getValue}) => {
                const date = getValue() as string;
                return formatDate(new Date(date).toLocaleString());
            },
        },
        {
            accessorKey: 'metadata.userAgent',
            header: 'Client Browser', enableSorting: true,
            cell: ({row}) => (
                <div className="max-w-[300px] text-xs text-gray-700">
                    {row.original.metadata?.userAgent}
                </div>
            ),
        },
        {
            accessorKey: 'feedback',
            header: 'Feedback', enableSorting: true,
            cell: ({row}) => (
                <div className="max-w-[400px] whitespace-pre-wrap">
                    {row.original.feedback}
                </div>
            ),
        },
        {
            accessorKey: 'files',
            header: 'Screenshots', enableSorting: true,
            cell: ({row}) => {
                const files = row.original.files || [];
                if (!files.length) return '';
                return (
                    <div className="flex space-x-2">
                        {files.map((file) => (
                            <button
                                className="btn btn-sm btn-icon btn-light btn-clear"
                                onClick={() => handleFileDownload(file.download_url, file.original_name)}
                            >
                                <KeenIcon icon="file-down"/>
                            </button>
                        ))}
                    </div>
                );
            },
        },
    ], [searchTerm]);

    if (loading) return <Loading loading={loading}/>;

    return (
        <div className="card card-grid min-w-full">
            <div className="card-header flex-wrap py-5">
                <h3 className="card-title">Feedbacks</h3>
                <div className="relative">
                    <KeenIcon
                        icon="magnifier"
                        className="text-md text-gray-500 absolute top-1/2 left-0 transform -translate-y-1/2 ml-3"
                    />
                    <input
                        type="text"
                        placeholder="Search feedbacks"
                        className="input input-sm pl-8"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="card-body">
                <DataGrid
                    key={searchTerm}
                    columns={columns}
                    data={filteredData}
                    pagination={{size: 10}}
                />
            </div>
        </div>
    );
};

export {Feedbacks};
